// Build-time constant — set by Vite via `define`. Empty string means
// same-origin (dev: Vite proxy handles /api and /ws). In prod, this is
// the public API origin (e.g. https://api.exegesis.example.com).
declare const __API_URL__: string;

const API_BASE = typeof __API_URL__ !== "undefined" ? __API_URL__ : "";

/** Build an absolute or same-origin URL for an /api/* path. */
export function apiUrl(path: string): string {
  return `${API_BASE}${path}`;
}

/** Default fetch options for cross-origin requests with credentials. */
const FETCH_OPTS: RequestInit = { credentials: "include" };

export interface WorkspaceFile {
  relativePath: string;
  isBinary: boolean;
  size: number;
  fileType?: string;
}

export interface UploadResult {
  sessionId: string;
  binaryPath: string;
  workDir: string;
  isWorkspace: boolean;
  uploadedFiles: string[];
  workspaceFiles: WorkspaceFile[];
  triage: { info: string | null; analysis: string | null };
}

/**
 * Upload one or more files. A single .zip gets auto-unpacked server-side.
 * Multiple files land in the same workspace directory. The server-side
 * Session then treats the workspace as a folder (matching CLI folder mode).
 */
export async function uploadBinary(files: File | File[]): Promise<UploadResult> {
  const arr = Array.isArray(files) ? files : [files];
  if (arr.length === 0) throw new Error("no files selected");
  const form = new FormData();
  for (const f of arr) form.append("file", f, f.name);
  const res = await fetch(apiUrl("/api/sessions"), {
    method: "POST",
    body: form,
    credentials: "include",
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`upload failed: ${res.status} ${text}`);
  }
  return res.json();
}

export interface Artifact {
  id: string;
  relativePath: string;
  source: "workspace" | "work";
  size: number;
  mtime: number;
}

export interface SessionSummary {
  sessionId: string;
  binaryPath: string;
  workDir: string;
  createdAt: number;
  lastActivity: number;
  turnCount: number;
  hasActivity: boolean;
}

export async function listRecentSessions(): Promise<SessionSummary[]> {
  const res = await fetch(apiUrl("/api/sessions"), FETCH_OPTS);
  if (!res.ok) return [];
  const { sessions } = (await res.json()) as { sessions: SessionSummary[] };
  return sessions;
}

export async function listArtifacts(sessionId: string): Promise<Artifact[]> {
  const res = await fetch(
    apiUrl(`/api/sessions/${sessionId}/artifacts`),
    FETCH_OPTS,
  );
  if (!res.ok) return [];
  const { artifacts } = (await res.json()) as { artifacts: Artifact[] };
  return artifacts;
}

export function artifactDownloadUrl(sessionId: string, artifactId: string): string {
  return apiUrl(
    `/api/sessions/${sessionId}/download?path=${encodeURIComponent(artifactId)}`,
  );
}

export async function deleteSession(sessionId: string): Promise<void> {
  await fetch(apiUrl(`/api/sessions/${sessionId}`), {
    method: "DELETE",
    credentials: "include",
  });
}
